import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { HelpCircle, Lock } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useAuth, formatApiError } from "@/contexts/AuthContext";
import { BRAND_LOGOS } from "@/components/PaymentLogos";

const COUNTRIES = ["United States", "Canada", "Mexico"];

// Payment methods matching user's spec — Visa/Mastercard/Amex under Credit
// card, then PayPal, Stripe. No Google Pay / Shop Pay / UnionPay / others.
const PAYMENT_METHODS = [
  {
    id: "card",
    label: "Credit card",
    logos: ["visa", "mastercard", "amex"],
  },
  { id: "paypal", label: "PayPal", logos: ["paypal"] },
  { id: "stripe", label: "Stripe", logos: ["stripe"] },
];

export default function CheckoutPage() {
  const { cart, refresh, checkout } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    email: user?.email || "",
    optIn: false,
    country: "United States",
    first_name: user?.name?.split(" ")[0] || "",
    last_name: user?.name?.split(" ").slice(1).join(" ") || "",
    address_line: "",
    apt: "",
    city: "",
    postal_code: "",
    phone: "",
    shipping_method: "standard",
    payment_method: "card",
    card_number: "",
    card_expiry: "",
    card_cvv: "",
    card_name: "",
    billing_same: true,
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const placeOrder = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await checkout({
        full_name: `${form.first_name} ${form.last_name}`.trim(),
        email: form.email,
        address_line:
          form.address_line + (form.apt ? `, ${form.apt}` : ""),
        city: form.city,
        country: form.country,
        postal_code: form.postal_code,
        payment_method: form.payment_method,
      });
      await refresh();
      toast.success("Order placed.");
      navigate("/checkout/success");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!cart || (cart.lines || []).length === 0) {
    return (
      <div className="max-w-[900px] mx-auto px-6 py-24 text-center">
        <p className="overline">Your bag is empty</p>
        <h1 className="font-serif text-3xl mt-3">Nothing to check out yet.</h1>
        <Link to="/shop" className="btn-primary inline-block mt-8">
          Continue shopping
        </Link>
      </div>
    );
  }

  const shippingCost = form.shipping_method === "expedite" ? 0 : 0;
  const totals = cart.totals || {};

  return (
    <div data-testid="checkout-page" className="bg-[#ffffff] min-h-screen">
      <div className="max-w-[1240px] mx-auto px-4 md:px-8 py-10 md:py-14 grid lg:grid-cols-[1.2fr_1fr] gap-10 lg:gap-16">
        {/* LEFT — form */}
        <form onSubmit={placeOrder} className="space-y-10" data-testid="checkout-form">
          {/* Contact */}
          <section>
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-xl text-black">Contact</h2>
              {!user && (
                <Link
                  to="/login"
                  data-testid="checkout-signin-link"
                  className="text-[12px] underline underline-offset-4 text-black hover:text-[#c9a84c]"
                >
                  Sign in
                </Link>
              )}
            </div>
            <Input
              testId="ck-email"
              placeholder="Email"
              value={form.email}
              onChange={(v) => update("email", v)}
              type="email"
              required
              trailing={<HelpCircle size={16} strokeWidth={1.4} className="text-black/40" />}
            />
            <label className="mt-3 flex items-center gap-2 text-[13px] text-black/70">
              <input
                type="checkbox"
                checked={form.optIn}
                onChange={(e) => update("optIn", e.target.checked)}
                data-testid="ck-optin"
                className="accent-[#c9a84c]"
              />
              Opt-in for product updates in your inbox.
            </label>
          </section>

          {/* Delivery */}
          <section>
            <h2 className="font-serif text-xl text-black">Delivery</h2>
            <Select
              testId="ck-country"
              label="Country / Region"
              value={form.country}
              onChange={(v) => update("country", v)}
              options={COUNTRIES}
            />
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Input testId="ck-first-name" placeholder="First name" value={form.first_name} onChange={(v) => update("first_name", v)} required />
              <Input testId="ck-last-name" placeholder="Last name" value={form.last_name} onChange={(v) => update("last_name", v)} required />
            </div>
            <Input testId="ck-address" placeholder="Address" value={form.address_line} onChange={(v) => update("address_line", v)} required />
            <Input testId="ck-apt" placeholder="Apartment, suite, etc. (optional)" value={form.apt} onChange={(v) => update("apt", v)} />
            <div className="grid grid-cols-2 gap-3 mt-3">
              <Input testId="ck-city" placeholder="City" value={form.city} onChange={(v) => update("city", v)} required />
              <Input testId="ck-postal" placeholder="Postal code (optional)" value={form.postal_code} onChange={(v) => update("postal_code", v)} />
            </div>
            <Input testId="ck-phone" placeholder="Phone" value={form.phone} onChange={(v) => update("phone", v)} />
          </section>

          {/* Shipping method */}
          <section>
            <h2 className="font-serif text-xl text-black">Shipping method</h2>
            <label
              data-testid="ck-shipping-standard"
              className={`mt-3 flex items-center justify-between border px-4 py-4 cursor-pointer transition-colors ${form.shipping_method === "standard" ? "border-black" : "border-black/20 hover:border-black/60"}`}
            >
              <span className="flex items-center gap-3">
                <input
                  type="radio"
                  name="shipping"
                  checked={form.shipping_method === "standard"}
                  onChange={() => update("shipping_method", "standard")}
                  className="accent-black"
                />
                <span className="text-sm text-black">Standard · 7 business days</span>
              </span>
              <span className="text-sm text-black">Complimentary</span>
            </label>
            <label
              data-testid="ck-shipping-expedite"
              className={`mt-2 flex items-center justify-between border px-4 py-4 cursor-pointer transition-colors ${form.shipping_method === "expedite" ? "border-black" : "border-black/20 hover:border-black/60"}`}
            >
              <span className="flex items-center gap-3">
                <input
                  type="radio"
                  name="shipping"
                  checked={form.shipping_method === "expedite"}
                  onChange={() => update("shipping_method", "expedite")}
                  className="accent-black"
                />
                <span className="text-sm text-black">Expedite · 3–4 business days</span>
              </span>
              <span className="text-sm text-black">Complimentary</span>
            </label>
          </section>

          {/* Payment */}
          <section>
            <h2 className="font-serif text-xl text-black">Payment</h2>
            <p className="mt-1 text-[12px] text-black/55">All transactions are secure and encrypted.</p>

            <div className="mt-4 border border-black/20" data-testid="payment-methods">
              {PAYMENT_METHODS.map((m, i) => {
                const active = form.payment_method === m.id;
                return (
                  <div key={m.id} className={`${i > 0 ? "border-t border-black/15" : ""}`}>
                    <label
                      data-testid={`ck-payment-${m.id}`}
                      className={`flex items-center justify-between px-4 py-4 cursor-pointer ${active ? "bg-black/[0.02]" : ""}`}
                    >
                      <span className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="payment"
                          value={m.id}
                          checked={active}
                          onChange={() => update("payment_method", m.id)}
                          className="accent-black"
                        />
                        <span className="text-sm text-black font-medium">{m.label}</span>
                      </span>
                      <span className="flex items-center gap-2" data-testid={`ck-logos-${m.id}`}>
                        {m.logos.map((l) => {
                          const Logo = BRAND_LOGOS[l];
                          return Logo ? <Logo key={l} /> : null;
                        })}
                      </span>
                    </label>
                    {active && m.id === "card" && (
                      <div className="px-4 pb-5 space-y-3" data-testid="ck-card-fields">
                        <Input testId="ck-card-number" placeholder="Card number" value={form.card_number} onChange={(v) => update("card_number", v)} trailing={<Lock size={14} strokeWidth={1.4} className="text-black/45" />} />
                        <div className="grid grid-cols-2 gap-3">
                          <Input testId="ck-card-expiry" placeholder="Expiration date (MM / YY)" value={form.card_expiry} onChange={(v) => update("card_expiry", v)} />
                          <Input testId="ck-card-cvv" placeholder="Security code" value={form.card_cvv} onChange={(v) => update("card_cvv", v)} trailing={<HelpCircle size={16} strokeWidth={1.4} className="text-black/40" />} />
                        </div>
                        <Input testId="ck-card-name" placeholder="Name on card" value={form.card_name} onChange={(v) => update("card_name", v)} />
                        <label className="flex items-center gap-2 text-[13px] text-black/75">
                          <input type="checkbox" checked={form.billing_same} onChange={(e) => update("billing_same", e.target.checked)} data-testid="ck-billing-same" className="accent-black" />
                          Use shipping address as billing address
                        </label>
                      </div>
                    )}
                    {active && m.id === "paypal" && (
                      <div className="px-4 pb-5 space-y-3" data-testid="ck-paypal-fields">
                        <p className="text-[12px] text-black/65 leading-relaxed">
                          After clicking &ldquo;Pay now&rdquo; you will be redirected to PayPal
                          to complete your purchase securely.
                        </p>
                        <Input testId="ck-paypal-email" type="email" placeholder="PayPal email (optional)" value={form.paypal_email || ""} onChange={(v) => update("paypal_email", v)} />
                      </div>
                    )}
                    {active && m.id === "stripe" && (
                      <div className="px-4 pb-5 space-y-3" data-testid="ck-stripe-fields">
                        <p className="text-[12px] text-black/65 leading-relaxed">
                          Card processed securely via Stripe. All fields are PCI-encrypted.
                        </p>
                        <Input testId="ck-stripe-card" placeholder="Card number" value={form.stripe_card || ""} onChange={(v) => update("stripe_card", v)} trailing={<Lock size={14} strokeWidth={1.4} className="text-black/45" />} />
                        <div className="grid grid-cols-2 gap-3">
                          <Input testId="ck-stripe-expiry" placeholder="MM / YY" value={form.stripe_expiry || ""} onChange={(v) => update("stripe_expiry", v)} />
                          <Input testId="ck-stripe-cvc" placeholder="CVC" value={form.stripe_cvc || ""} onChange={(v) => update("stripe_cvc", v)} />
                        </div>
                        <Input testId="ck-stripe-zip" placeholder="Billing ZIP" value={form.stripe_zip || ""} onChange={(v) => update("stripe_zip", v)} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <p className="mt-4 text-[10px] tracking-[0.18em] uppercase text-black/45">
              Payment processing goes live once merchant keys are added.
            </p>
          </section>

          {/* Submit */}
          <button
            type="submit"
            disabled={submitting}
            data-testid="ck-pay-now"
            className="w-full bg-black text-white py-4 text-[13px] tracking-[0.22em] uppercase hover:bg-[#c9a84c] hover:text-black transition-colors disabled:opacity-40"
          >
            {submitting ? "Placing order…" : `Pay now · $${(totals.total || 0).toFixed(2)}`}
          </button>
        </form>

        {/* RIGHT — order summary */}
        <aside
          className="lg:sticky lg:top-24 self-start bg-[#fafafa] border border-black/10 p-6"
          data-testid="checkout-summary"
        >
          <ul className="space-y-4">
            {cart.lines.map((l) => (
              <li key={l.line_id} className="flex gap-4 items-start">
                <div className="relative w-16 h-16 bg-white border border-black/10 overflow-hidden shrink-0">
                  {l.image && <img src={l.image} alt="" className="w-full h-full object-cover" />}
                  <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-black text-white text-[10px] flex items-center justify-center">
                    {l.quantity}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-medium text-black leading-tight">{l.title}</p>
                  <p className="text-[11px] text-black/55 mt-1">
                    {l.size ? `${l.size}` : ""}
                    {l.body_type ? ` · ${l.body_type}` : ""}
                  </p>
                </div>
                <span className="text-[13px] text-black">${(l.price * l.quantity).toFixed(2)}</span>
              </li>
            ))}
          </ul>

          <div className="mt-5 flex gap-2">
            <input
              placeholder="Discount code or gift card"
              data-testid="ck-discount"
              className="flex-1 border border-black/20 px-3 py-2.5 text-[12px] focus:outline-none focus:border-black"
            />
            <button
              type="button"
              className="px-5 py-2.5 bg-black/[0.06] text-[11px] tracking-[0.18em] uppercase text-black/60 hover:bg-black/10"
            >
              Apply
            </button>
          </div>

          <dl className="mt-6 space-y-2 text-[13px]">
            <div className="flex justify-between text-black/75">
              <dt>Subtotal</dt>
              <dd>${(totals.subtotal || 0).toFixed(2)}</dd>
            </div>
            <div className="flex justify-between text-black/75">
              <dt className="inline-flex items-center gap-1">
                Shipping <HelpCircle size={13} strokeWidth={1.3} className="text-black/40" />
              </dt>
              <dd>{shippingCost === 0 ? "Complimentary" : `$${shippingCost.toFixed(2)}`}</dd>
            </div>
            <div className="pt-3 border-t border-black/10 flex items-baseline justify-between">
              <dt className="text-black font-medium">Total</dt>
              <dd className="text-black font-serif text-2xl">
                <span className="text-[11px] tracking-[0.22em] text-black/50 mr-2">USD</span>
                ${(totals.total || 0).toFixed(2)}
              </dd>
            </div>
          </dl>
        </aside>
      </div>
      <Toaster theme="dark" position="top-center" />
    </div>
  );
}

const Input = ({ testId, value, onChange, trailing, ...rest }) => (
  <div className="mt-3 relative">
    <input
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-black/20 px-4 py-3.5 text-sm focus:outline-none focus:border-black placeholder:text-black/45"
      {...rest}
    />
    {trailing && <span className="absolute right-3 top-1/2 -translate-y-1/2">{trailing}</span>}
  </div>
);

const Select = ({ testId, label, value, onChange, options }) => (
  <div className="mt-3 relative">
    <label className="absolute left-4 top-2 text-[10px] tracking-[0.18em] uppercase text-black/50">
      {label}
    </label>
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-black/20 pl-4 pr-8 pt-6 pb-2.5 text-sm focus:outline-none focus:border-black appearance-none bg-white"
    >
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  </div>
);
